import {
  AdditionalConfiguration,
  ReportSection,
  ReportSectionConfigurationBase,
  ReportSectionConfigurationControlType,
  ReportType,
  ReportViewModelSection
} from '@capital-access/reporting/common';

export class TargetingConfiguration extends ReportSectionConfigurationBase {
  typeName = 'TargetingConfiguration';
  nodataLocalizationKeyName = 'targeting';
  readonly reportSection = ReportSection.Targeting;
  readonly applicableReportTypes = [ReportType.Contact, ReportType.Institution];
  readonly reportViewModelSections = [ReportViewModelSection.InvestorProfile_InvestorTargetingInfo];

  private findSubSection = (sectionName: string, subSectionName: string) =>
    this.availableDataSections
      ?.find(section => section.name === sectionName)
      ?.subSections?.find(subSection => subSection.name === subSectionName);

  override additionalConfiguration: AdditionalConfiguration = {
    isGraphSelected: true
  };

  private allDataSectionsWithoutGraphs() {
    if (
      this.availableDataSections.every(section => section.isSelected) &&
      this.availableDataSections.every(section =>
        section.subSections ? section.subSections.every(subSection => subSection.isSelected) : true
      ) &&
      !this.additionalConfiguration.isGraphSelected
    ) {
      return 1;
    }
    return 0;
  }

  private onlyTargetingTextDataPoints() {
    const allTextDataPointsSelected = this.availableDataSections
      .filter(section => section?.type !== ReportSectionConfigurationControlType.Table)
      .every(section => {
        return (
          (section.name === 'targetingOpportunityRank' ||
            section.name === 'targetingInstitutionRating' ||
            section.name === 'targetingBestFund' ||
            section.name === 'targetingSuggestedContact') &&
          section.isSelected
        );
      });

    const allGridsNotSelected = this.availableDataSections
      .filter(section => section?.type === ReportSectionConfigurationControlType.Table)
      .every(
        section =>
          (section.name === 'targetingGrid' || section.name === 'targetingSuitabilityFactorsGrid') &&
          !section.isSelected
      );

    if (allTextDataPointsSelected && allGridsNotSelected) {
      return 1;
    }
    return 0;
  }

  private onlyTargetingGridWithOneColumnSelected(): number {
    const targetingGridInstitution = this.findSubSection('targetingGrid', 'targetingGridInstitution');
    const targetingGridBestFund = this.findSubSection('targetingGrid', 'targetingGridBestFund');

    if (
      this.availableDataSections.find(section => section.name === 'targetingGrid')?.isSelected &&
      (targetingGridInstitution?.isSelected || targetingGridBestFund?.isSelected) &&
      !(targetingGridInstitution?.isSelected && targetingGridBestFund?.isSelected)
    ) {
      return 1;
    }
    return 0;
  }

  private onlyTargetingGridWithBothColumnsSelected(): number {
    const targetingGridInstitution = this.findSubSection('targetingGrid', 'targetingGridInstitution');
    const targetingGridBestFund = this.findSubSection('targetingGrid', 'targetingGridBestFund');

    if (
      this.availableDataSections.find(section => section.name === 'targetingGrid')?.isSelected &&
      targetingGridInstitution?.isSelected &&
      targetingGridBestFund?.isSelected
    ) {
      return 2;
    }
    return 0;
  }

  private onlySuitabilityFactorsGridWithOneColumnSelected(): number {
    const targetingSuitabilityFactorsGridInstitution = this.findSubSection(
      'targetingSuitabilityFactorsGrid',
      'targetingSuitabilityFactorsGridInstitution'
    );
    const targetingSuitabilityFactorsGridBestFund = this.findSubSection(
      'targetingSuitabilityFactorsGrid',
      'targetingSuitabilityFactorsGridBestFund'
    );

    const a = this.availableDataSections.find(
      section => section.name === 'targetingSuitabilityFactorsGrid'
    )?.isSelected;
    const b =
      targetingSuitabilityFactorsGridInstitution?.isSelected || targetingSuitabilityFactorsGridBestFund?.isSelected;

    const c = !(
      targetingSuitabilityFactorsGridInstitution?.isSelected && targetingSuitabilityFactorsGridBestFund?.isSelected
    );

    if (a && b && c) {
      return this.additionalConfiguration?.isGraphSelected ? 2 : 1;
    }
    return 0;
  }

  private onlySuitabilityFactorsGridWithBothColumnsSelected(): number {
    const targetingSuitabilityFactorsGridInstitution = this.findSubSection(
      'targetingSuitabilityFactorsGrid',
      'targetingSuitabilityFactorsGridInstitution'
    );
    const targetingSuitabilityFactorsGridBestFund = this.findSubSection(
      'targetingSuitabilityFactorsGrid',
      'targetingSuitabilityFactorsGridBestFund'
    );

    if (
      this.availableDataSections.find(section => section.name === 'targetingSuitabilityFactorsGrid')?.isSelected &&
      targetingSuitabilityFactorsGridInstitution?.isSelected &&
      targetingSuitabilityFactorsGridBestFund?.isSelected
    ) {
      return this.additionalConfiguration?.isGraphSelected ? 4 : 2;
    }
    return 0;
  }

  get minSize(): number {
    const sizeCalculators = [
      this.allDataSectionsWithoutGraphs.bind(this),
      this.onlyTargetingTextDataPoints.bind(this),
      this.onlyTargetingGridWithOneColumnSelected.bind(this),
      this.onlyTargetingGridWithBothColumnsSelected.bind(this),
      this.onlySuitabilityFactorsGridWithOneColumnSelected.bind(this),
      this.onlySuitabilityFactorsGridWithBothColumnsSelected.bind(this)
    ];

    let colSize = 1;
    for (const calculate of sizeCalculators) {
      const result = calculate();
      if (colSize < result) {
        colSize = result;
      }
    }

    return colSize;
  }

  get maxSize(): number {
    return 4;
  }

  get defaultSize(): number {
    return this.maxSize;
  }

  readonly availableDataSections = [
    {
      name: 'targetingOpportunityRank',
      displayName: 'Opportunity Rank',
      isSelected: true,
      canConfigure: true
    },
    {
      name: 'targetingInstitutionRating',
      displayName: 'Institution Rating',
      isSelected: true,
      canConfigure: true,
      unit: 2
    },
    {
      name: 'targetingSuggestedContact',
      displayName: 'Suggested Contact',
      isSelected: true,
      canConfigure: true,
      unit: 2
    },
    {
      name: 'targetingBestFund',
      displayName: 'Best Fund',
      isSelected: true,
      canConfigure: true,
      unit: 2
    },
    {
      name: 'targetingGrid',
      displayName: 'Institution & Best Fund Details',
      type: ReportSectionConfigurationControlType.Table,
      isSelected: true,
      canConfigure: true,
      subSections: [
        {
          name: 'targetingGridInstitution',
          displayName: 'Institution',
          isSelected: true,
          canConfigure: true,
          groupName: 'column'
        },
        {
          name: 'targetingGridBestFund',
          displayName: 'Best Fund',
          isSelected: true,
          canConfigure: true,
          groupName: 'column'
        },
        {
          name: 'targetingGridSuitabilityScore',
          displayName: 'Suitability Score',
          isSelected: true,
          canConfigure: true,
          groupName: 'row'
        },
        {
          name: 'targetingGridPurchasingPower',
          displayName: 'Purchasing Power',
          isSelected: true,
          canConfigure: true,
          groupName: 'row'
        },
        {
          name: 'targetingGridCountryExposure',
          displayName: 'Country Exposure',
          isSelected: true,
          canConfigure: true,
          groupName: 'row'
        },
        {
          name: 'targetingGridMarketCapExposure',
          displayName: 'Market Cap Exposure',
          isSelected: true,
          canConfigure: true,
          groupName: 'row'
        },
        {
          name: 'targetingGridIndustryExposure',
          displayName: 'Industry Exposure',
          isSelected: true,
          canConfigure: true,
          groupName: 'row'
        }
      ]
    },
    {
      name: 'targetingSuitabilityFactorsGrid',
      displayName: 'Suitability Factors',
      type: ReportSectionConfigurationControlType.Table,
      isSelected: true,
      canConfigure: true,
      subSections: [
        {
          name: 'targetingSuitabilityFactorsGridInstitution',
          displayName: 'Institution',
          isSelected: true,
          canConfigure: true,
          groupName: 'column'
        },
        {
          name: 'targetingSuitabilityFactorsGridBestFund',
          displayName: 'Best Fund',
          isSelected: true,
          canConfigure: true,
          groupName: 'column'
        },
        {
          name: 'targetingSuitabilityFactorsGridTopScores',
          displayName: 'Top Scores',
          isSelected: true,
          canConfigure: true,
          groupName: 'row'
        },
        {
          name: 'targetingSuitabilityFactorsGridBottomScores',
          displayName: 'Bottom Scores',
          isSelected: true,
          canConfigure: true,
          groupName: 'row'
        },
        {
          name: 'targetingSuitabilityFactorsGridPreferences',
          displayName: 'Preferences',
          isSelected: true,
          canConfigure: true,
          groupName: 'row'
        }
      ]
    }
  ];

  override readonly configurationControls = [
    {
      name: 'targetingSuitabilityFactorGridGraph',
      displayName: 'Targeting Suitability Factor Graph',
      type: 'graphControl'
    }
  ];

  get name(): string {
    if (this.appliedReportType === ReportType.Contact) {
      return 'contactTargeting';
    } else if (this.appliedReportType === ReportType.Institution) {
      return 'institutionTargeting';
    } else {
      throw new Error('Report Type not supported');
    }
  }

  get displayName(): string {
    if (this.appliedReportType === ReportType.Contact || this.appliedReportType === ReportType.Institution) {
      return 'Targeting';
    } else {
      throw new Error('Report Type not supported');
    }
  }
}

export interface TargetingData {
  [ReportViewModelSection.InvestorProfile_InvestorTargetingInfo]: {
    targeting: Targeting;
    culture: string;
    language: string;
  };
}
export interface Targeting {
  bestFundTargeting: BestFundTargeting;
  targetRank: number;
  priorityRating: string;
  bestFund: BestFund;
  id: number;
  suitabilityScore: number;
  purchasingPower: number;
  countryExposure: string;
  industryExposure: string;
  marketCapExposure: string;
  contact: Contact;
  topContributingScores: FundamentalAllocation[];
  bottomContributingScores: FundamentalAllocation[];
  topInvestmentPreferrencesFactors: FundamentalAllocation[];
  customTargetRating: CustomTargetRating;
  isCustomTargetingFtEnabled: boolean;
}

export interface BestFundTargeting {
  id: number;
  suitabilityScore: number;
  purchasingPower: number;
  countryExposure: string;
  industryExposure: string;
  marketCapExposure: string;
  topContributingScores: FundamentalAllocation[];
  bottomContributingScores: FundamentalAllocation[];
  topInvestmentPreferrencesFactors: FundamentalAllocation[];
}

export interface BestFund {
  id: number;
  name: string;
}

export interface Contact {
  id: string;
  name: string;
  email: string;
  isEsg: boolean;
}

export interface FundamentalAllocation {
  fundamental: FundamentalType;
  score?: number;
  rawScore?: number;
  securityDecileIndex?: number;
  fundamentalAllocations: number[];
}

export enum FundamentalType {
  DebtToCapital = 'DebtToCapital',
  DividendYield = 'DividendYield',
  EbitIntexp = 'EbitIntexp',
  EvEbitda = 'EvEbitda',
  PriceToEarningsForward = 'PriceToEarningsForward',
  LtExpGrowth = 'LtExpGrowth',
  MarginGrowth = 'MarginGrowth',
  MarketCap = 'MarketCap',
  Pr52WkPct = 'Pr52WkPct',
  PriceToEquity = 'PriceToEquity',
  PriceToSales = 'PriceToSales',
  Roe = 'Roe',
  EpsGrowth = 'EpsGrowth',
  SalesGrowth = 'SalesGrowth',
  PriceToCashFlow = 'PriceToCashFlow'
}

export enum PriorityRating {
  NR = 'NR',
  HM = 'HM',
  H = 'H',
  HT = 'HT',
  T1 = 'T1',
  T2 = 'T2',
  T3 = 'T3',
  SWF = 'SWF',
  NT = 'NT'
}

export interface CustomTargetRating {
  lastModifiedUser: number;
  lastModifiedDate: string;
  customRating: PriorityRating;
}
